import {
  Root,
  Trigger,
  Portal,
  Content,
  Arrow,
  TooltipProps as RadixTooltipProps,
  Provider,
  TooltipContentProps,
} from '@radix-ui/react-tooltip';
import React from 'react';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { RichText } from '../RichText/RichText';
import { Text } from '../Text/Text';

export interface TooltipProps extends RadixTooltipProps {
  className?: string;
  contentProps?: TooltipContentProps;
  description?: string;
  descriptionClassName?: string;
  hasArrow?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  title?: string;
  trigger: JSX.Element;
  variant?: 'light' | 'dark';
}

const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      className = '',
      contentProps = {},
      description,
      descriptionClassName,
      hasArrow = true,
      maxWidth = 'xs',
      title,
      trigger,
      variant = 'dark',
      ...tooltipProps
    },
    ref
  ) => {
    const maxWidthClass = {
      xs: 'max-w-xs',
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
    }[maxWidth];
    const tooltipClassesMerged = twMerge(
      `rounded shadow-level1-soft p-3 z-1000 ${
        variant === 'light' ? 'bg-white text-black' : 'bg-black text-white'
      }`,
      className
    );
    const titleFontWeight = description ? 'semibold' : 'regular';
    return (
      <Provider>
        <Root delayDuration={0} {...tooltipProps}>
          <Trigger ref={ref} asChild>
            {trigger}
          </Trigger>
          {(title || description) && (
            <Portal>
              <Content
                data-testid="tooltip-content"
                className={tooltipClassesMerged}
                sideOffset={4}
                hideWhenDetached
                {...contentProps}
              >
                {hasArrow && (
                  <Arrow
                    className={`fill-current drop-shadow-level1-soft mask-clip ${
                      variant === 'light' ? 'text-white' : 'text-black'
                    }`}
                    style={{ clipPath: 'inset(0 -10px -10px -10px)' }}
                  />
                )}
                <div
                  className={`flex flex-col ${
                    title ? 'gap-y-1.5' : 'gap-0'
                  } ${maxWidthClass}`}
                >
                  {title && (
                    <Text fontSize="sm" fontWeight={titleFontWeight} asChild>
                      <span>{title}</span>
                    </Text>
                  )}
                  {description && (
                    <RichText
                      content={description}
                      className={descriptionClassName}
                    />
                  )}
                </div>
              </Content>
            </Portal>
          )}
        </Root>
      </Provider>
    );
  }
);

export default Tooltip;
Tooltip.displayName = 'Tooltip';
